import type { ProjectWidget } from '@vd/types';

/**
 * Which sites the snippet was seen on that the widget refuses (t-FZOecS).
 * An owner pasted the snippet, saw "Snippet detected", and then nothing
 * they tried could send a report, because the site was not on the allowed
 * list: the only sign was a small "Allow this site" button in a list.
 *
 * The server's rule (apps/api/src/widgets/config.ts, `isOriginAllowed` and
 * `resolveOriginPolicy`): with no allowed sites, any site may send the
 * report form, but pointing at the page and recording need the site on the
 * list; with any allowed site, every other site is refused outright.
 */

/**
 * Whether `origin` satisfies one allowed-list entry: exactly, or through a
 * `https://*.acme.com` entry that covers exactly one more label, as the
 * server's `matchesEntry` reads it.
 */
export function originMatches(origin: string, entry: string): boolean {
  if (!entry.includes('*')) return origin === entry;
  const match = /^(https?:\/\/)\*\.(.+?)(:\d{1,5})?$/.exec(entry);
  if (!match) return false;
  const [, scheme, suffix, port = ''] = match;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (`${url.protocol}//` !== scheme) return false;
  if ((url.port ? `:${url.port}` : '') !== port) return false;
  const host = url.hostname.toLowerCase();
  const dotSuffix = `.${suffix!.toLowerCase()}`;
  if (!host.endsWith(dotSuffix)) return false;
  const label = host.slice(0, -dotSuffix.length);
  return label.length > 0 && !label.includes('.');
}

export const isAllowed = (origin: string, allowed: readonly string[]): boolean =>
  allowed.some((entry) => originMatches(origin, entry));

/**
 * Why the widget cannot be turned on yet (Jason, 28 Oct, Toado ticket
 * t-a4DgDT): it is on only for the sites it lists, and the server refuses to
 * turn it on with none, in the same words.
 */
export const NEEDS_A_SITE = 'Add your site under Allowed sites before turning the widget on.';

/**
 * Saved on with no site listed, from before one was required (t-a4DgDT): the
 * one widget that takes reports from any website. Read from the SAVED widget,
 * so an unsaved edit never makes a restricted widget look like one.
 */
export function isLegacyAnySite(widget: Pick<ProjectWidget, 'enabled' | 'allowedOrigins'>): boolean {
  return widget.enabled && widget.allowedOrigins.length === 0;
}

export type AllowedSitesGap =
  /** An allowed site is listed, and the snippet has loaded on one of them or not loaded anywhere yet. */
  | { kind: 'ok' }
  /** No allowed site saved. `legacy`: on from before a site was required, so the form works anywhere. */
  | { kind: 'no-sites'; legacy: boolean; seen: number }
  /** Sites are allowed, but none of those the snippet loaded on is among them: every report is refused. */
  | { kind: 'not-allowed' }
  /** The unsaved edits allow a site that closes the gap: it only needs saving. */
  | { kind: 'save' }
  /** On, saved with sites, every one taken off unsaved: a save the server refuses (t-a4DgDT review round 1). */
  | { kind: 'removing' };

/**
 * Whether the widget has an allowed site it can work on (Jason, 30 Oct): the
 * one thing the notice at the top of its settings is for. Judged from the
 * SAVED widget, since the site gets what was saved, with the draft deciding
 * only whether a save would fix it.
 */
export function allowedSitesGap(
  widget: Pick<ProjectWidget, 'enabled' | 'allowedOrigins' | 'verification'>,
  staged: readonly string[],
): AllowedSitesGap {
  const seen = widget.verification.origins.map((entry) => entry.origin);
  const saved = widget.allowedOrigins;
  const noneSaved = saved.length === 0;
  const noneSeenAllowed = !noneSaved && seen.length > 0 && !seen.some((origin) => isAllowed(origin, saved));
  if (!noneSaved && !noneSeenAllowed) {
    return widget.enabled && staged.length === 0 ? { kind: 'removing' } : { kind: 'ok' };
  }
  const draftFixes = staged.length > 0 && (seen.length === 0 || seen.some((origin) => isAllowed(origin, staged)));
  if (draftFixes) return { kind: 'save' };
  return noneSaved
    ? { kind: 'no-sites', legacy: isLegacyAnySite(widget), seen: seen.length }
    : { kind: 'not-allowed' };
}
